import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import api from '@/services/api';
import { renovacaoRuleService } from '@/services/renovacaoRuleService';
import RenovacaoHistoryTable from './RenovacaoHistoryTable';
import RenovacaoHistoryDialog from './RenovacaoHistoryDialog';

interface TagOption {
  id: number;
  tag_name: string;
  tag_code: string;
}

interface Props {
  clientId: number;
}

const DEFAULTS = {
  codigo_situacao: 0,
  tag_id: 0,
  intervalo_minutos: 10,
  delay_segundos: 2,
  hora_inicio: '08:00',
  hora_fim: '18:00',
  is_active: false,
  permitir_reenvio: false,
};

function trimSeconds(time: string): string {
  // '08:00:00' → '08:00' (input type=time)
  return (time || '').slice(0, 5);
}

export const RenovacaoTab: React.FC<Props> = ({ clientId }) => {
  const [form, setForm] = useState<typeof DEFAULTS>(DEFAULTS);
  const [tags, setTags] = useState<TagOption[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await renovacaoRuleService.getByClient(clientId);
        if (!mounted) return;
        setForm({
          codigo_situacao: result.codigo_situacao || 0,
          tag_id: result.tag_id || 0,
          intervalo_minutos: result.intervalo_minutos || 10,
          delay_segundos: result.delay_segundos ?? 2,
          hora_inicio: trimSeconds(result.hora_inicio || '08:00:00'),
          hora_fim: trimSeconds(result.hora_fim || '18:00:00'),
          is_active: !!result.is_active,
          permitir_reenvio: !!result.permitir_reenvio,
        });
      } catch (e) {
        console.error('[Renovacao] erro ao carregar régua:', e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [clientId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingTags(true);
      try {
        const res = (await api.get(
          `/tags/client/${clientId}/type/RENOVACAO`
        )) as { success: boolean; data?: TagOption[] };
        if (!mounted) return;
        setTags(res.success ? res.data || [] : []);
      } catch (e) {
        console.error('[Renovacao] erro ao carregar tags:', e);
      } finally {
        if (mounted) setLoadingTags(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [clientId]);

  function validate(): string | null {
    if (form.codigo_situacao <= 0) return 'Informe um código de situação válido (> 0).';
    if (!form.tag_id) return 'Selecione uma tag de Renovação.';
    if (form.intervalo_minutos < 1 || form.intervalo_minutos > 1440)
      return 'Intervalo deve estar entre 1 e 1440 minutos.';
    if (form.delay_segundos < 0 || form.delay_segundos > 120)
      return 'Delay entre envios deve estar entre 0 e 120 segundos.';
    if (form.hora_inicio >= form.hora_fim)
      return 'A hora de início deve ser menor que a hora de fim.';
    return null;
  }

  async function handleSave() {
    const err = validate();
    if (err) {
      toast({ title: 'Erro de validação', description: err, variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await renovacaoRuleService.upsert(clientId, {
        codigo_situacao: form.codigo_situacao,
        tag_id: form.tag_id,
        intervalo_minutos: form.intervalo_minutos,
        delay_segundos: form.delay_segundos,
        hora_inicio: form.hora_inicio,
        hora_fim: form.hora_fim,
        is_active: form.is_active,
        permitir_reenvio: form.permitir_reenvio,
      });
      toast({ title: 'Salvo', description: 'Régua de renovação atualizada.' });
    } catch (e: any) {
      const msg = e?.message || 'Erro ao salvar.';
      toast({ title: 'Erro ao salvar', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Régua de Renovação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              />
              <Label>{form.is_active ? 'Ativo' : 'Inativo'}</Label>
            </div>

            <div className="flex items-start gap-3">
              <Switch
                checked={form.permitir_reenvio}
                onCheckedChange={(v) => setForm((f) => ({ ...f, permitir_reenvio: v }))}
              />
              <div>
                <Label>Permitir reenvio (mesmo se já enviado)</Label>
                <p className="text-xs text-gray-500">
                  Reenvia para associados que já receberam, sempre que voltarem ao
                  código de situação-gatilho.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tag de mensagem</Label>
                <Select
                  value={form.tag_id ? String(form.tag_id) : ''}
                  onValueChange={(v) => setForm((f) => ({ ...f, tag_id: Number(v) }))}
                  disabled={loadingTags}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingTags ? 'Carregando...' : 'Selecione uma tag'} />
                  </SelectTrigger>
                  <SelectContent>
                    {tags.length === 0 && !loadingTags && (
                      <SelectItem value="0" disabled>
                        Nenhuma tag — cadastre em Mensagens
                      </SelectItem>
                    )}
                    {tags.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.tag_name || t.tag_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Código de situação</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.codigo_situacao || ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, codigo_situacao: Number(e.target.value) || 0 }))
                  }
                />
                <p className="text-xs text-gray-500">
                  Veículos com este código serão processados pelo cron. Use um código
                  <strong> diferente de 1</strong> e <strong>diferente do usado em Boas-Vindas</strong>
                  {' '}(ambas as réguas movem o veículo para a situação 1 após o envio).
                </p>
              </div>

              <div>
                <Label>Intervalo (minutos)</Label>
                <Input
                  type="number"
                  min={1}
                  max={1440}
                  value={form.intervalo_minutos}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      intervalo_minutos: Number(e.target.value) || 1,
                    }))
                  }
                />
                <p className="text-xs text-gray-500">
                  A cada quantos minutos o cron verifica veículos.
                </p>
              </div>

              <div>
                <Label>Delay entre envios (segundos)</Label>
                <Input
                  type="number"
                  min={0}
                  max={120}
                  value={form.delay_segundos}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      delay_segundos: Number(e.target.value) || 0,
                    }))
                  }
                />
                <p className="text-xs text-gray-500">
                  Pausa entre uma mensagem e a próxima, para não sobrecarregar a
                  plataforma de comunicação. 0 = sem pausa.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Hora início</Label>
                  <Input
                    type="time"
                    value={form.hora_inicio}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, hora_inicio: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Hora fim</Label>
                  <Input
                    type="time"
                    value={form.hora_fim}
                    onChange={(e) => setForm((f) => ({ ...f, hora_fim: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <RenovacaoHistoryTable
        clientId={clientId}
        onOpenFullHistory={() => setHistoryOpen(true)}
      />

      <RenovacaoHistoryDialog
        clientId={clientId}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </div>
  );
};

export default RenovacaoTab;
